import { useNavigate } from 'react-router-dom'
import styles from './mhlShared.module.scss'
import { Pill } from './SourceTabs'
import type { MhlLead } from './mhlTypes'
import {
    agentDisplay,
    audiologyLabel,
    audiologyTone,
    esignLabel,
    esignTone,
    formatDate,
    formatDateTime,
    quantumLabel,
    referralLabel,
    referralTone,
    reviewLabel,
    reviewTone,
} from './mhlFormatting'
import { MHL_PAGE_SIZE, TablePagination, usePagedRows } from './TablePagination'

interface Props {
    leads: MhlLead[]
    loading?: boolean
    emptyMessage?: string
    pageSize?: number
    /** Show the Agent + Review columns (dashboard-onboarded leads). */
    showReview?: boolean
    /** Show the claim-site referral Source column (webform leads). */
    showReferral?: boolean
    compact?: boolean
}

export const LeadsTable = ({
    leads,
    loading = false,
    emptyMessage = 'No leads found.',
    pageSize = MHL_PAGE_SIZE,
    showReview = true,
    showReferral = false,
    compact = false,
}: Props) => {
    const navigate = useNavigate()
    const { rows, page, totalPages, setPage, pageSize: size } = usePagedRows(leads, pageSize)

    const open = (lead: MhlLead) => navigate(`/military-hearing-loss/client/${lead.client_id}`)

    const colCount = (showReview ? 8 : 6) + (showReferral ? 1 : 0)

    return (
        <div className={styles.tableWrap}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>Client</th>
                        <th>Contact</th>
                        <th>Audiology</th>
                        <th>Quantum</th>
                        {showReview && <th>Review</th>}
                        <th>E-sign</th>
                        {showReview && <th>Submitted by</th>}
                        {showReferral && <th>Source</th>}
                        <th>Submitted</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={colCount} className={styles.emptyState}>
                                Loading…
                            </td>
                        </tr>
                    ) : rows.length === 0 ? (
                        <tr>
                            <td colSpan={colCount} className={styles.emptyState}>
                                {emptyMessage}
                            </td>
                        </tr>
                    ) : (
                        rows.map((lead) => {
                            const linkSent = !!lead.esign.link_sent_at
                            return (
                                <tr
                                    key={lead.id}
                                    className={`${styles.rowClickable} ${lead.review_status === 'rejected' ? styles.rowMuted : ''}`}
                                    onClick={() => open(lead)}
                                >
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackPrimary}>
                                                {lead.firstname} {lead.lastname}
                                            </span>
                                            <span className={styles.stackSecondary}>
                                                DOB {formatDate(lead.dob)}
                                                {lead.nationalInsurance ? ` · NI ${lead.nationalInsurance}` : ''}
                                                {lead.postcode ? ` · ${lead.postcode}` : ''}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackSecondary}>{lead.email || '—'}</span>
                                            <span className={styles.stackSecondary}>{lead.telephone || '—'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.stackCell}>
                                            <Pill tone={audiologyTone(lead.audiology)}>{audiologyLabel(lead.audiology)}</Pill>
                                            {showReview && !compact && (
                                                <span className={styles.stackSecondary}>
                                                    {lead.hasAudiogram ? 'Audiogram attached' : 'No audiogram'}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <Pill tone={lead.quantum === 'above' ? 'good' : lead.quantum === 'below' ? 'bad' : 'warn'}>
                                            {quantumLabel(lead.quantum)}
                                        </Pill>
                                    </td>
                                    {showReview && (
                                        <td>
                                            <div className={styles.stackCell}>
                                                <Pill tone={reviewTone(lead.review_status)} dot>
                                                    {reviewLabel(lead.review_status)}
                                                </Pill>
                                                {lead.reviewed_at && !compact && (
                                                    <span className={styles.stackSecondary}>{formatDateTime(lead.reviewed_at)}</span>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                    <td>
                                        <div className={styles.stackCell}>
                                            <Pill tone={esignTone(lead.esign.signed, linkSent)}>
                                                {esignLabel(lead.esign.signed, lead.esign.link_sent_at, lead.esign.link_opened_at)}
                                            </Pill>
                                            {!compact && (lead.esign.signature_date || lead.esign.link_sent_at) && (
                                                <span className={styles.stackSecondary}>
                                                    {formatDateTime(lead.esign.signature_date || lead.esign.link_sent_at)}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    {showReview && (
                                        <td>
                                            <div className={styles.stackCell}>
                                                <span className={styles.stackSecondary}>{agentDisplay(lead.added_by_name, lead.added_by)}</span>
                                                {lead.added_by_org && !compact && (
                                                    <span className={styles.stackSecondary}>{lead.added_by_org}</span>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                    {showReferral && (
                                        <td>
                                            <Pill tone={referralTone(lead.lead_source)}>{referralLabel(lead.lead_source)}</Pill>
                                        </td>
                                    )}
                                    <td>
                                        <span className={styles.stackSecondary}>{formatDateTime(lead.submitted_at)}</span>
                                    </td>
                                </tr>
                            )
                        })
                    )}
                </tbody>
            </table>

            <TablePagination
                page={page}
                totalPages={totalPages}
                total={leads.length}
                pageSize={size}
                onChange={setPage}
            />
        </div>
    )
}
